import { QuestionnaireQuestion, ValueType } from '@core/generated/gql-types';

const getValidOptions = (
  options?: QuestionnaireQuestion['options'],
): Array<{ label: string; value: string }> => {
  if (!options) {
    return [];
  }

  return options.filter(
    (option): option is { label: string; value: string } =>
      option !== null && !!option.value && !!option.label,
  );
};

/** Converts a stored label back to the raw form value used by QuestionnaireSection. */
export const labelToRawValue = (
  question: QuestionnaireQuestion,
  storedLabel: string,
): string => {
  const valueType = question.answerType || ValueType.String;
  const options = getValidOptions(question.options);

  if (valueType === ValueType.Boolean) {
    switch (storedLabel) {
      case 'Yes':
        return 'true';
      case 'No':
        return 'false';
      default:
        return storedLabel;
    }
  }

  if (options.length > 0) {
    if (valueType === ValueType.Array) {
      const labels = storedLabel.split(',').map((label) => label.trim());
      const values = labels
        .map((label) => options.find((option) => option.label === label)?.value)
        .filter((value): value is string => Boolean(value));

      return values.join(',');
    }

    const option = options.find((entry) => entry.label === storedLabel);
    return option?.value || storedLabel;
  }

  return storedLabel;
};

/** Mirrors backend QuestionnaireQuestionClass.valueToLabel for update payloads. */
export const rawValueToLabel = (
  question: QuestionnaireQuestion,
  rawValue: string,
): string => {
  const valueType = question.answerType || ValueType.String;
  let backendValue = rawValue;

  if (valueType === ValueType.Array) {
    const arrayValues = rawValue.split(',').filter(Boolean);
    backendValue = JSON.stringify(arrayValues);
  }

  const valuesToValidate =
    valueType === ValueType.Array ? JSON.parse(backendValue) : [backendValue];
  const options = getValidOptions(question.options);
  const labels: string[] = [];

  if (options.length > 0) {
    for (const value of valuesToValidate) {
      const option = options.find((entry) => entry.value === value);
      if (option) {
        labels.push(option.label);
      }
    }
  } else {
    labels.push(backendValue);
  }

  return labels.join(', ');
};

export const buildQuestionnaireCustomAttributes = (
  questions: QuestionnaireQuestion[],
  responses: Record<string, string>,
) => {
  return questions.map((question) => ({
    ref: question.questionTitle,
    value: rawValueToLabel(question, responses[question.id] || ''),
  }));
};

export const buildResponsesFromCustomAttributes = (
  questions: QuestionnaireQuestion[],
  customAttributes?: Array<{
    ref?: string | null;
    value?: string | null;
  } | null> | null,
): Record<string, string> => {
  if (!customAttributes?.length) {
    return {};
  }

  const responses: Record<string, string> = {};

  for (const question of questions) {
    const attribute = customAttributes.find(
      (entry) => entry?.ref === question.questionTitle,
    );

    if (attribute?.value) {
      responses[question.id] = labelToRawValue(question, attribute.value);
    }
  }

  return responses;
};
